import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from 'react-intl';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import { Sticky } from 'react-sticky';
import { mapDispatchToProps } from '../redux/language';
import MenuIcon from '../icons/MenuIcon';
import CloseIcon from '../icons/CloseIcon';
import TextInput from '../TextInput';
import LinkButton from '../LinkButton';
import Typography from '../Typography';
import { getTextColorsFromSection } from '../lib/colors';
import Image from '../Image';
import Spacer from '../Spacer';
import Section from '../Section';

export const MENU_HEIGHT_MOBILE = 64;

class MenuSection extends Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    theme: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    site: PropTypes.object.isRequired,
    section: PropTypes.object.isRequired,
    scrollToSection: PropTypes.func,
    language: PropTypes.string.isRequired,
    intl: intlShape.isRequired,
    selectLanguage: PropTypes.func.isRequired,
  };

  static defaultProps = {
    scrollToSection: null,
  };

  state = {
    menuOpened: false,
  };

  componentDidMount() {
    this.root = ReactDOM.findDOMNode(this);
    this.parent = this.root.parentNode;
  }

  toggleMenu = () => {
    this.setState({ menuOpened: !this.state.menuOpened });
  };

  closeMenu = () => {
    this.setState({ menuOpened: false });
  };

  onMenuItemClick = event => {
    event.preventDefault();

    const { scrollToSection } = this.props;
    const targetId = event.target.hash.substr(1);

    if (scrollToSection) {
      scrollToSection(targetId);
    } else {
      const target = document.getElementById(targetId);
      const sections = this.parent.children;

      let menuOffset = 0;
      if (this.state.menuOpened || (sections[0] !== target && sections[1] !== target)) {
        menuOffset = this.state.menuOpened ? MENU_HEIGHT_MOBILE : this.root.clientHeight || 0;
      }

      this.parent.scrollTo(0, target.offsetTop - menuOffset);
    }

    this.closeMenu();
  };

  getMappedItems = items => {
    const { section, site } = this.props;
    const colors = getTextColorsFromSection(site, section);
    return (
      items &&
      items.map((item, index) => (
        <li key={item.id || index}>
          <Typography
            component="a"
            href={`#${item.section}`}
            onClick={this.onMenuItemClick}
            style={colors.text}
          >
            {item.title}
          </Typography>
        </li>
      ))
    );
  };

  getLanguageSelector = () => {
    const { classes, event, selectLanguage, language } = this.props;
    return (
      <TextInput
        variant="select"
        input={{
          value: language,
          onChange: event => selectLanguage(event.target.value),
        }}
        className={classes.languagesSelect}
      >
        {event.languages &&
          event.languages.map(language => {
            return (
              <option key={language} value={language}>
                {language.toUpperCase()}
              </option>
            );
          })}
      </TextInput>
    );
  };

  getMenuButton = () => {
    const { section, event, site } = this.props;
    const { buttons } = section.data;
    const button = buttons && buttons[0];
    return (
      button && (
        <LinkButton
          section={section}
          site={site}
          event={event}
          button={button}
          style={section.styles.buttons && section.styles.buttons[0]}
        >
          {button.title}
        </LinkButton>
      )
    );
  };

  render() {
    const { classes, section, event, site, intl } = this.props;
    const { brand, items } = section.data;
    const { menuOpened } = this.state;
    const listItems = this.getMappedItems(items) || [];
    const languageSelector = this.getLanguageSelector();
    const menuButton = this.getMenuButton();
    const colors = getTextColorsFromSection(site, section);

    const hasMultipleLanguages = event.languages && event.languages.length > 1;

    const Icon = menuOpened ? CloseIcon : MenuIcon;

    return (
      <Sticky relative>
        {({ style, isSticky }) => (
          <Section
            site={site}
            section={section}
            className={classes.contentWrapper}
            rootClassName={classNames(classes.root, isSticky && classes.stuck)}
            style={style}
          >
            <div className={classes.iconsWrapper}>
              {Boolean(listItems.length || hasMultipleLanguages) && (
                <Icon className={classes.menu} onClick={this.toggleMenu} style={colors.text} />
              )}

              {Boolean(brand && brand.url) && (
                <Image
                  className={classNames(classes.brandImage, isSticky && classes.brandImageShown)}
                  src={brand.url}
                  alt={intl.formatMessage({ id: 'eventBrandAlt' })}
                />
              )}
            </div>

            <div className={classes.items}>
              <ul className={classes.navItems}>{listItems}</ul>
              <Spacer />
              {menuButton}
            </div>
            {hasMultipleLanguages && languageSelector}
            {Boolean(menuOpened && (listItems.length || hasMultipleLanguages)) && (
              <ul className={classNames(classes.navItems, classes.hideMenu)}>
                {[...listItems, menuButton, hasMultipleLanguages && languageSelector]}
              </ul>
            )}
          </Section>
        )}
      </Sticky>
    );
  }
}

const stylesheet = theme => ({
  root: {
    zIndex: 4,
    [theme.breakpoints.down('md')]: {
      minHeight: MENU_HEIGHT_MOBILE,
      flexDirection: 'column',
      position: 'absolute',
      top: 0,
      boxShadow: theme.shadows[2],
    },
  },
  stuck: {
    boxShadow: theme.shadows[2],
  },
  contentWrapper: {
    display: 'flex',
    alignItems: 'center',
    padding: `${2 * theme.spacing.unit}px ${3 * theme.spacing.unit}px`,
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
  },
  iconsWrapper: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      flex: 1,
    },
  },
  menu: {
    marginRight: 2 * theme.spacing.unit,
    cursor: 'pointer',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  brandImage: {
    marginRight: 2 * theme.spacing.unit,
    display: 'none',
    width: 'auto',
    height: 64,
    [theme.breakpoints.down('md')]: {
      display: 'inline-block',
      height: 32,
    },
  },
  brandImageShown: {
    display: 'inline-block',
  },
  items: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  navItems: {
    paddingLeft: 0,
    display: 'flex',
    listStyleType: 'none',
    '& li': {
      marginRight: 4 * theme.spacing.unit,
    },
    '& a': {
      textDecoration: 'none',
      fontSize: 16,
    },
    [theme.breakpoints.down('md')]: {
      display: 'block',
      '& li': {
        padding: `${2 * theme.spacing.unit}px ${3 * theme.spacing.unit}px`,
        marginRight: 0,
      },
    },
  },
  hideMenu: {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  languagesSelect: {
    marginLeft: theme.spacing.unit,
    background: 'none',
    border: 0,
    height: 52,
    '$contentWrapper > &': {
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
  },
});

export default compose(
  injectIntl,
  connect(
    null,
    mapDispatchToProps,
  ),
  injectSheet(stylesheet),
)(MenuSection);



// WEBPACK FOOTER //
// ./src/site-render/src/MenuSection/MenuSection.js